import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, ArrowUpRight, GripVertical } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import {
  fetchProjects, createProject, deleteProject, reorderProjects, mediaUrl, formatApiError,
} from "../../lib/api";
import ProjectEditor from "./ProjectEditor";
import SiteTextPanel from "./SiteTextPanel";
import CategoriesEditor from "./CategoriesEditor";
import SecurityPanel from "./SecurityPanel";
import SessionNotice from "./SessionNotice";

export default function AdminDashboard() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(true);
  const [editorDirty, setEditorDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const dragRef = useRef(null);

  const guard = (fn) => {
    if (editorDirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    fn();
  };

  const refresh = useCallback(async () => {
    const p = await fetchProjects();
    setProjects(p);
    return p;
  }, []);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    if (editorDirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    try {
      const p = await createProject({ name: newName.trim(), description: "", category: "Object" });
      setNewName("");
      await refresh();
      setSelectedId(p.id);
      toast.success("Project created");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its images?`)) return;
    try {
      await deleteProject(id);
      if (selectedId === id) setSelectedId(null);
      await refresh();
      toast.success("Project deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDropProject = (targetIdx) => {
    const from = dragRef.current;
    dragRef.current = null;
    setDragIdx(null);
    if (from === null || from === targetIdx) return;
    const arr = [...projects];
    const [moved] = arr.splice(from, 1);
    arr.splice(targetIdx, 0, moved);
    setProjects(arr);
    reorderProjects(arr.map((p) => p.id)).catch((err) => { toast.error(formatApiError(err)); refresh(); });
  };

  const logout = () => { signOut(); navigate("/admin/login"); };
  const selected = projects.find((p) => p.id === selectedId);

  return (
    <div className="min-h-screen bg-[var(--vsl-bg)] text-[var(--vsl-fg)]">
      <header className="sticky top-0 z-40 bg-[var(--vsl-bg)]/85 backdrop-blur-xl border-b border-[var(--vsl-border)]">
        <SessionNotice />
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img src="/logo-vessel.png" alt="VESSEL" className="h-5" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--vsl-muted)]">CMS</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/" target="_blank" rel="noreferrer" data-testid="view-site"
              className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)] transition-colors inline-flex items-center gap-1">
              View site <ArrowUpRight size={12} />
            </a>
            <span className="font-mono text-[10px] text-[var(--vsl-muted)] hidden sm:block">{user?.email}</span>
            <button data-testid="logout-btn" onClick={() => guard(logout)}
              className="font-mono text-[10px] tracking-[0.2em] uppercase inline-flex items-center gap-1.5 hover:text-[var(--vsl-limestone)] transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 flex gap-8 border-t border-[var(--vsl-border)] overflow-x-auto no-scrollbar">
          {["projects", "categories", "text", "security"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => guard(() => setTab(t))}
              className={`font-mono text-[11px] tracking-[0.2em] uppercase py-3 border-b-2 -mb-px transition-colors whitespace-nowrap shrink-0 ${tab === t ? "border-[var(--vsl-fg)] text-[var(--vsl-fg)]" : "border-transparent text-[var(--vsl-muted)] hover:text-[var(--vsl-fg)]"}`}>
              {t === "projects" ? "Projects" : t === "categories" ? "Categories" : t === "text" ? "Site Text" : "Security"}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {tab === "text" ? (
          <SiteTextPanel />
        ) : tab === "security" ? (
          <SecurityPanel />
        ) : tab === "categories" ? (
          <CategoriesEditor />
        ) : (
          <div className="grid lg:grid-cols-[380px_1fr] gap-8">
            <div>
              <form onSubmit={handleCreate} className="flex gap-2 mb-6" data-testid="create-project-form">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New project name"
                  data-testid="new-project-name"
                  className="flex-1 bg-transparent border border-[var(--vsl-border)] px-3 py-2.5 font-mono text-sm outline-none focus:border-[var(--vsl-fg)]" />
                <button type="submit" data-testid="add-project-btn"
                  className="bg-[var(--vsl-fg)] text-[var(--vsl-bg)] px-4 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[var(--vsl-limestone)] transition-colors">
                  <Plus size={14} /> Add
                </button>
              </form>

              {loading ? (
                <p className="font-mono text-xs text-[var(--vsl-muted)]">Loading…</p>
              ) : (
                <ul className="space-y-2" data-testid="admin-project-list">
                  {projects.map((p, i) => (
                    <li key={p.id} data-testid={`project-row-${p.slug}`}
                      draggable
                      onDragStart={() => { dragRef.current = i; setDragIdx(i); }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDropProject(i)}
                      onDragEnd={() => { dragRef.current = null; setDragIdx(null); }}
                      className={`flex items-center gap-2 border p-2 transition-opacity ${dragIdx === i ? "opacity-40" : ""} ${selectedId === p.id ? "border-[var(--vsl-fg)] bg-white/[0.03]" : "border-[var(--vsl-border)]"}`}>
                      <GripVertical size={15} className="text-[var(--vsl-muted)] shrink-0 cursor-move" data-testid={`project-grip-${p.slug}`} />
                      <button onClick={() => guard(() => setSelectedId(p.id))} data-testid={`select-project-${p.slug}`}
                        className="flex items-center gap-3 flex-1 text-left min-w-0">
                        <div className="w-12 h-12 bg-[#141312] overflow-hidden shrink-0">
                          {(p.cover_type === "video" ? p.cover_poster : p.cover) &&
                            <img src={mediaUrl(p.cover_type === "video" ? p.cover_poster : p.cover)} alt="" className="w-full h-full object-cover" />}
                        </div>
                        <div className="min-w-0">
                          <p className="font-mono text-sm truncate">{p.name}</p>
                          <p className="font-mono text-[10px] text-[var(--vsl-muted)] uppercase tracking-wider">{p.category} · {p.images.length} media</p>
                        </div>
                      </button>
                      <button onClick={() => handleDelete(p.id, p.name)} data-testid={`delete-project-${p.slug}`}
                        className="text-[var(--vsl-muted)] hover:text-red-400 transition-colors shrink-0"><Trash2 size={15} /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              {selected ? (
                <ProjectEditor
                  key={selected.id}
                  project={selected}
                  allProjects={projects}
                  onDirtyChange={setEditorDirty}
                  onChange={async () => { const p = await refresh(); return p; }}
                />
              ) : (
                <div className="border border-dashed border-[var(--vsl-border)] h-full min-h-[300px] flex items-center justify-center">
                  <p className="font-mono text-xs text-[var(--vsl-muted)]">Select a project to edit</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
